import * as React from "react";
import Dropzone, { type FileRejection } from "react-dropzone";
import { Loader2, Upload } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { formatBytes } from "@/lib/utils";

interface FileUploaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onUpload?: (files: File[]) => Promise<void>;
  accept?: Record<string, string[]>;
  maxSize?: number;
  disabled?: boolean;
  isLoading?: boolean;
}

export function FileUploader({
  onUpload,
  accept = { "image/*": [] },
  maxSize = 1024 * 1024 * 2, // 2MB
  disabled = false,
  isLoading = false,
  className,
  ...dropzoneProps
}: FileUploaderProps) {
  const { toast } = useToast();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFiles] = React.useState<File[]>([]);

  const onDrop = React.useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length === 0) return;

      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(({ file }) => {
          toast({
            title: `File ${file.name} was rejected`,
            variant: "destructive",
          });
        });
        return;
      }

      if (onUpload) {
        try {
          await onUpload(acceptedFiles);
          toast({
            title: "File uploaded successfully",
            variant: "default",
          });
          setFiles([]);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          let errorMessage = "Failed to upload file. Please try again later.";

          if (error?.body?.detail) {
            errorMessage = error.body.detail;
          }

          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: Infinity,
          });
        }
      }
    },
    [onUpload, toast],
  );

  return (
    <div className="relative flex flex-col gap-6 overflow-hidden">
      <Dropzone
        onDrop={onDrop}
        accept={accept}
        maxSize={maxSize}
        maxFiles={1}
        multiple={false}
        disabled={disabled || isLoading}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={`group relative grid h-52 w-full cursor-pointer place-items-center rounded-lg border-2 border-dashed border-muted-foreground/25 px-5 py-2.5 text-center transition hover:bg-muted/25 ${isDragActive ? "border-muted-foreground/50" : ""} ${disabled || isLoading ? "pointer-events-none opacity-60" : ""} ${className || ""}`}
            {...dropzoneProps}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
              <div className="rounded-full border border-dashed p-3">
                {isLoading ? (
                  <Loader2 className="size-7 animate-spin text-muted-foreground" aria-hidden="true" />
                ) : (
                  <Upload className="size-7 text-muted-foreground" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-col gap-px">
                <p className="font-medium text-muted-foreground">
                  {isLoading
                    ? "Uploading file..."
                    : isDragActive
                      ? "Drop the file here"
                      : "Drag 'n' drop a file here, or click to select"}
                </p>
                <p className="text-sm text-muted-foreground/70">Maximum file size: {formatBytes(maxSize)}</p>
              </div>
            </div>
          </div>
        )}
      </Dropzone>
    </div>
  );
}
